// frontend/src/features/bapb/sections/LeaseUpCalculator.jsx
import React, { useEffect, useMemo, useState } from "react";

export default function LeaseUpCalculator({
  value,                       // persisted lease_up object (optional)
  onChange,                    // (nextLeaseUpObject) => void
  pgiAnnual,                   // number (annual PGI at full/stabilized tenancy)
  stabilizedVacancyPct,        // number, e.g. 10 means 10% stabilized vacancy => target occ = 90
  defaultDiscountAnnualPct,    // number, e.g. your applied cap * 100
  opexAnnual,                  // number (annual operating expenses — for carrying cost calc)
  totalBuildingSF,             // number (from cover page — used directly for TI calc)
}) {
  // ---------- helpers ----------
  const clean = (s) => (s == null ? "" : String(s).replace(/[^\d.-]/g, ""));
  const n = (v) => {
    if (v === "" || v == null || v === "-") return 0;
    const s = String(v).replace(/[$,%\s,]/g, "");
    const num = Number(s);
    return Number.isFinite(num) ? num : 0;
  };
  const fmtPct = (v) => {
    const d = clean(v);
    if (!d && d !== 0) return "";
    const num = Number(d);
    return Number.isFinite(num) ? `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}%` : "";
  };
  const fmt$0 = (v) => {
    const d = Number(v || 0);
    if (!Number.isFinite(d)) return "";
    return `$${Math.round(d).toLocaleString()}`;
  };

  // ---------- local form ----------
  const [form, setForm] = useState({
    current_occupancy_pct: value?.current_occupancy_pct ?? "",
    absorption_months: value?.absorption_months ?? 12,
    discount_rate_annual_pct: value?.discount_rate_annual_pct ?? (defaultDiscountAnnualPct ?? 0),
    leasing_commission_pct: value?.leasing_commission_pct ?? "4",
    ti_per_sf: value?.ti_per_sf ?? "5",
    free_rent_months: value?.free_rent_months ?? "2",
  });

  // Total SF comes from cover page, not user input
  const totalSF = Math.max(0, Number(totalBuildingSF) || 0);

  // Target occupancy derives from stabilized vacancy (no manual edit needed)
  const targetOccPct = useMemo(() => {
    const v = Math.max(0, Math.min(100, Number(stabilizedVacancyPct) || 0));
    return Math.max(0, Math.min(100, 100 - v));
  }, [stabilizedVacancyPct]);

  // Core calc: enhanced NPV with commissions, TI, free rent, and carrying costs.
  const calcResult = useMemo(() => {
    const PGI = Number(pgiAnnual) || 0;
    const OPEX = Number(opexAnnual) || 0;
    const occ0 = Math.max(0, Math.min(100, n(form.current_occupancy_pct)));
    const occT = Math.max(0, Math.min(100, targetOccPct));
    const months = Math.max(1, Math.floor(n(form.absorption_months)) || 1);
    const rAnnual = Math.max(0, n(form.discount_rate_annual_pct)) / 100;
    const rMonthly = rAnnual / 12;

    const commPct = Math.max(0, n(form.leasing_commission_pct)) / 100;
    const tiPerSF = Math.max(0, n(form.ti_per_sf));
    const freeRentMo = Math.max(0, Math.floor(n(form.free_rent_months)) || 0);

    // Derived: fraction of building to fill + vacant SF for TI calc
    const vacantPct = Math.max(0, (occT - occ0)) / 100;
    const vacantSF = totalSF > 0 ? totalSF * vacantPct : 0;
    const midMonth = Math.max(1, Math.round(months / 2));

    // --- Component 1: NPV of lost rental income ---
    let npvLostIncome = 0;
    if (PGI > 0 && occ0 < occT) {
      const step = (occT - occ0) / months;
      for (let t = 1; t <= months; t += 1) {
        const occ_t = occ0 + step * (t - 1);
        const shortfallPct = Math.max(0, (occT - occ_t) / 100);
        const cf = (PGI / 12) * shortfallPct;
        npvLostIncome += cf / Math.pow(1 + rMonthly, t);
      }
    }

    // --- Component 2: Carrying costs (OpEx on vacant space during absorption) ---
    let npvCarrying = 0;
    if (OPEX > 0 && occ0 < occT) {
      const step = (occT - occ0) / months;
      for (let t = 1; t <= months; t += 1) {
        const occ_t = occ0 + step * (t - 1);
        const shortfallPct = Math.max(0, (occT - occ_t) / 100);
        const cf = (OPEX / 12) * shortfallPct;
        npvCarrying += cf / Math.pow(1 + rMonthly, t);
      }
    }

    // --- Component 3: Free rent concessions ---
    // Tenants sign throughout absorption; average signing at midpoint.
    // Each gets freeRentMo months of zero income. Lump sum discounted from midpoint.
    let npvFreeRent = 0;
    if (PGI > 0 && freeRentMo > 0 && occ0 < occT) {
      const absorbedPgiMonthly = (PGI / 12) * vacantPct;
      const freeRentCost = absorbedPgiMonthly * freeRentMo;
      npvFreeRent = freeRentCost / Math.pow(1 + rMonthly, midMonth);
    }

    // --- Component 4: Leasing commissions (% of first year gross rent on absorbed space) ---
    let commissionNPV = 0;
    if (PGI > 0 && commPct > 0 && occ0 < occT) {
      const absorbedAnnualRent = PGI * vacantPct;
      const commissionCost = absorbedAnnualRent * commPct;
      commissionNPV = commissionCost / Math.pow(1 + rMonthly, midMonth);
    }

    // --- Component 5: Tenant improvements ($/SF on vacant SF, derived from cover SF + occupancy) ---
    let tiNPV = 0;
    if (vacantSF > 0 && tiPerSF > 0) {
      const tiCost = vacantSF * tiPerSF;
      tiNPV = tiCost / Math.pow(1 + rMonthly, midMonth);
    }

    const totalNPV = npvLostIncome + npvCarrying + npvFreeRent + commissionNPV + tiNPV;

    return { npvLostIncome, npvCarrying, npvFreeRent, commissionNPV, tiNPV, totalNPV, vacantSF };
  }, [pgiAnnual, opexAnnual, form.current_occupancy_pct, form.absorption_months,
      form.discount_rate_annual_pct, totalSF, form.leasing_commission_pct,
      form.ti_per_sf, form.free_rent_months, targetOccPct]);

  // Push back to parent so it can save this inside the Income section payload
  useEffect(() => {
    onChange?.({
      current_occupancy_pct: n(form.current_occupancy_pct),
      absorption_months: Math.max(1, Math.floor(n(form.absorption_months)) || 1),
      discount_rate_annual_pct: n(form.discount_rate_annual_pct),
      target_occupancy_pct: targetOccPct,
      total_sf: totalSF,
      leasing_commission_pct: n(form.leasing_commission_pct),
      ti_per_sf: n(form.ti_per_sf),
      free_rent_months: Math.max(0, Math.floor(n(form.free_rent_months)) || 0),
      // component breakdown
      npv_lost_income: calcResult.npvLostIncome,
      npv_carrying: calcResult.npvCarrying,
      npv_free_rent: calcResult.npvFreeRent,
      npv_commissions: calcResult.commissionNPV,
      npv_ti: calcResult.tiNPV,
      deduction: calcResult.totalNPV,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, targetOccPct, totalSF, calcResult]);

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3 bg-slate-50">
      <div className="text-sm font-medium">Lease-Up Calculator</div>

      {/* Row 1: Core inputs */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Current Occupancy (%)"
          value={form.current_occupancy_pct}
          onChange={(v)=>setForm(prev=>({ ...prev, current_occupancy_pct: fmtPct(v) }))}
          placeholder="e.g., 75%"
          inputMode="decimal"
        />
        <Field
          label="Absorption (months)"
          value={String(form.absorption_months ?? "")}
          onChange={(v)=>setForm(prev=>({ ...prev, absorption_months: v.replace(/[^\d]/g, "") }))}
          placeholder="e.g., 18"
          inputMode="numeric"
        />
        <Field
          label="Discount Rate - Annual (%)"
          value={fmtPct(form.discount_rate_annual_pct)}
          onChange={(v)=>setForm(prev=>({ ...prev, discount_rate_annual_pct: fmtPct(v) }))}
          placeholder="e.g., 9% (yield rate)"
          inputMode="decimal"
        />
      </div>

      {/* Row 2: Cost inputs */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Leasing Commission (%)"
          value={form.leasing_commission_pct}
          onChange={(v)=>setForm(prev=>({ ...prev, leasing_commission_pct: clean(v) }))}
          placeholder="e.g., 4%"
          inputMode="decimal"
        />
        <Field
          label="TI Allowance ($/SF)"
          value={form.ti_per_sf}
          onChange={(v)=>setForm(prev=>({ ...prev, ti_per_sf: clean(v) }))}
          placeholder="e.g., 5"
          inputMode="decimal"
        />
        <Field
          label="Free Rent (months)"
          value={String(form.free_rent_months ?? "")}
          onChange={(v)=>setForm(prev=>({ ...prev, free_rent_months: v.replace(/[^\d]/g, "") }))}
          placeholder="e.g., 2"
          inputMode="numeric"
        />
      </div>

      {/* Derived info */}
      <div className="text-xs text-slate-600 flex flex-wrap gap-x-4">
        <span>Target Occupancy: <strong>{targetOccPct.toLocaleString(undefined, { maximumFractionDigits: 2 })}%</strong></span>
        {calcResult.vacantSF > 0 && (
          <span>Vacant SF to absorb: <strong>{Math.round(calcResult.vacantSF).toLocaleString()}</strong></span>
        )}
      </div>

      {/* Breakdown */}
      <div className="rounded-md bg-white border border-slate-200 px-3 py-2 text-xs space-y-1">
        <div className="flex justify-between"><span className="text-slate-500">Lost Rental Income (NPV)</span><span>{fmt$0(calcResult.npvLostIncome)}</span></div>
        {calcResult.npvCarrying > 0 && (
          <div className="flex justify-between"><span className="text-slate-500">Carrying Costs on Vacant Space (NPV)</span><span>{fmt$0(calcResult.npvCarrying)}</span></div>
        )}
        {calcResult.npvFreeRent > 0 && (
          <div className="flex justify-between"><span className="text-slate-500">Free Rent Concessions (NPV)</span><span>{fmt$0(calcResult.npvFreeRent)}</span></div>
        )}
        {calcResult.commissionNPV > 0 && (
          <div className="flex justify-between"><span className="text-slate-500">Leasing Commissions (NPV)</span><span>{fmt$0(calcResult.commissionNPV)}</span></div>
        )}
        {calcResult.tiNPV > 0 && (
          <div className="flex justify-between"><span className="text-slate-500">Tenant Improvements (NPV)</span><span>{fmt$0(calcResult.tiNPV)}</span></div>
        )}
        <div className="flex justify-between border-t border-slate-200 pt-1 text-sm font-medium">
          <span>Total Lease-Up Deduction (NPV)</span>
          <strong>{fmt$0(calcResult.totalNPV)}</strong>
        </div>
      </div>
    </div>
  );
}

// Small local field component (kept here to avoid external deps)
function Field({ label, value, onChange, placeholder, inputMode }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        value={value ?? ""}
        onChange={(e)=>onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        className="w-full rounded-lg border border-gray-300 px-3 py-2"
      />
    </div>
  );
}
